import DemandTrendPanel from '../components/ecommerce/DemandTrendPanel.vue'
import ProductInsightsCards from '../components/ecommerce/ProductInsightsCards.vue'

import type {
  ReportFilter,
  ReportLayoutConfig,
  SectionBuild,
  SectionDropdown,
} from '@/features/reports'

// v3 `configs/Ecommerce/ProductInsights.js` (154 ln) + the render truth of
// `views/App/Ecommerce/ProductInsights.vue` (204 ln). Port notes:
// - The v3 config's layout entries were anchors + search metadata; the REAL section configs
//   lived in the view (`o-report-config :section=…`) and in the config file's named exports.
//   Both are folded here so the layout is complete as data.
// - ORP-18953 closed the page work: the KPI cards and Demand Trend ride `component` sections
//   (they own their data paths — contract §12's `scrollIntoView` jump lives inside the cards),
//   and Best Seller's v3 "Grouping" select (which swapped partition AND dimensions, beyond
//   `SectionDropdown`) is modelled as two top tabs whose `build` swaps both.
// - v3 CONFIG-vs-VIEW drift not ported: the config cites `overview` for Products with Issues
//   and `merchantcenter_bestsellers_product` for Best Seller / Demand Trend; the view gates and
//   requests `merchantcenter_product_issues` and `product_bestsellers`/`brand_bestsellers`
//   (`ProductInsights.vue:110–121`). The render truth wins.
// - ORP-18953's approved follow-up corrects the issue-type regex to match every value containing
//   a Latin letter. The legacy trailing `r` silently under-counted Product Issues.
// - v3 `hideTotal`/`addMetrics`/`orderedMetrics` wait for ORP-18643 column shaping (seed
//   precedent); `fieldValues` derive from chart dimensions/metrics in v4; `search_value`
//   blobs have no v4 section-level home (module 15 pending).

const PRODUCT_INSIGHTS_REPORT_ID = 17

// The approved issue-type contract matches every issue value that contains a Latin letter.
const PRODUCT_ISSUES_FILTERS: ReportFilter[] = [
  { field: 'product_issue_type', match_mode: 'regex', value: '.*[A-Za-z].*' },
]

const productIssuesBuild: SectionBuild = () => ({
  id: '17-product-with-issues',
  reportId: PRODUCT_INSIGHTS_REPORT_ID,
  scheduleTabs: ['merchantcenter_product_issues'],
  filters: PRODUCT_ISSUES_FILTERS,
})

// v3 `COMPETITION_INSIGHTS_CONFIG`.
const priceSensitivityBuild: SectionBuild = () => ({
  id: '17-price-sensitivity-analysis',
  reportId: PRODUCT_INSIGHTS_REPORT_ID,
  scheduleTabs: ['overview'],
  filters: [{ field: 'suggested_price', match_mode: 'greater_than', value: 0 }],
  chartDimensions: ['product_image', 'product_name'],
  chartMetrics: ['product_price', 'suggested_price'],
})

// v3 `TOP_COMPETITOR_TYPES[].field` — the machine tab name keys the wire value.
const TOP_COMPETITOR_TRAFFIC_SOURCE: Record<string, string> = {
  Ads: 'ADS',
  All: 'ALL',
  Organic: 'ORGANIC',
}

// v3 `TOP_COMPETITOR_DROPDOWNS[].tabs` — the selected view names the request partitions.
const TOP_COMPETITOR_VIEW_TABS = ['competitor_view', 'top_merchant_view']
const COMPETITOR_BENCHMARK_VIEW_TABS = ['competitor_benchmark_view']

// v3 bound the select to `o-custom-select label="Grouping"`; option 1 was the default and
// carried the `type = top_merchant_view` filter, option 2 carried none.
const TOP_COMPETITOR_DROPDOWNS: SectionDropdown[] = [
  {
    filter: { field: 'type', matchMode: 'equals' },
    labelKey: 'commerceAnalytics.productInsights.grouping',
    options: [
      {
        labelKey: 'commerceAnalytics.productInsights.topCompetitors.topCompetitorView',
        value: 'top_merchant_view',
      },
      { labelKey: 'commerceAnalytics.productInsights.topCompetitors.competitorBenchmarkView' },
    ],
  },
]

// v3 `topCompetitorConfig(tab, dropdownValue)` — the id keeps v3's exact persistence key
// (`17-top-competitors-${tab.name}-${dropdownValue.name}`), reconstructed from the applied
// dropdown filter (the `type` filter travels only with the Top Competitor View option).
const topCompetitorsBuild: SectionBuild = ({ tab, dropdownFilters }) => {
  const topCompetitorView = dropdownFilters.some((filter) => filter.field === 'type')
  return {
    id: `17-top-competitors-${tab.name}-${
      topCompetitorView ? 'Top Competitor View' : 'Competitor Benchmark View'
    }`,
    reportId: PRODUCT_INSIGHTS_REPORT_ID,
    scheduleTabs: topCompetitorView ? TOP_COMPETITOR_VIEW_TABS : COMPETITOR_BENCHMARK_VIEW_TABS,
    view: 'chart',
    chartType: 'scatter',
    chartDimensions: ['domain'],
    chartMetrics: ['page_overlap_rate', 'higher_position_rate'],
    sortField: 'competitive_rank',
    filters: [
      {
        field: 'traffic_source',
        match_mode: 'equals',
        value: TOP_COMPETITOR_TRAFFIC_SOURCE[tab.name] ?? 'ADS',
      },
      ...dropdownFilters,
    ],
  }
}

const TOP_COMPETITOR_AVAILABILITY_TABS = [
  'competitor_view',
  'top_merchant_view',
  'competitor_benchmark_view',
]

// v3 Best Seller (`ProductInsights.vue:42–70`): one `o-report-config` whose "Grouping" select
// swapped the schedule tab, the `report_type` filter AND the dimension set together. Two top
// tabs express the same request matrix; the v3 default selection was 'product'. The persistence
// id stays v3's single `17-best-sellers` for both groupings (v3 never varied it).
const BEST_SELLER_TAB_DATA: Record<string, { partition: string; dimensions: string[] }> = {
  Product: { partition: 'product_bestsellers', dimensions: ['product_name', 'product_brand'] },
  Brand: { partition: 'brand_bestsellers', dimensions: ['product_brand'] },
}

const bestSellerBuild: SectionBuild = ({ tab }) => {
  const grouping = tab.name === 'Brand' ? 'brand' : 'product'
  const tabData = BEST_SELLER_TAB_DATA[tab.name] ?? BEST_SELLER_TAB_DATA['Product']!
  return {
    id: '17-best-sellers',
    reportId: PRODUCT_INSIGHTS_REPORT_ID,
    scheduleTabs: [tabData.partition],
    dimensions: tabData.dimensions,
    filters: [{ field: 'report_type', match_mode: 'equals', value: grouping }],
  }
}

export const PRODUCT_INSIGHTS: ReportLayoutConfig = [
  {
    name: 'Overview',
    labelKey: 'commerceAnalytics.productInsights.tabs.overview',
    sections: [
      {
        // ORP-18953: the 4 KPI cards (no v3 DOM anchor — the cards were bare page markup).
        // Schedule-less tab: the availability filter keeps it, the component self-gates
        // (a read whose schedule is absent contributes 0 without requesting).
        id: 'productInsightsCards',
        component: ProductInsightsCards,
        tabs: [{ name: 'cards' }],
      },
      {
        // v3 DOM anchor `#productIssues` (`ProductInsights.vue:22`).
        id: 'productIssues',
        titleKey: 'commerceAnalytics.productInsights.sections.issues',
        reportId: PRODUCT_INSIGHTS_REPORT_ID,
        hideChannels: true,
        build: productIssuesBuild,
        tabs: [{ name: 'Products with Issues', tabs: ['merchantcenter_product_issues'] }],
      },
      {
        // v3 DOM anchor `#demandIncreasedProducts` (sic — v3 named the Best Seller card so).
        // Per-tab gating is a deliberate defect fix over v3, which offered both grouping
        // options whenever `product_bestsellers|category_bestsellers` existed and let the
        // brand option fire a doomed request when its partition was absent.
        id: 'demandIncreasedProducts',
        titleKey: 'commerceAnalytics.productInsights.sections.bestSeller',
        reportId: PRODUCT_INSIGHTS_REPORT_ID,
        topTabMode: true,
        hideChannels: true,
        build: bestSellerBuild,
        tabs: [
          {
            name: 'Product',
            labelKey: 'commerceAnalytics.productInsights.bestSeller.product',
            tabs: ['product_bestsellers'],
          },
          {
            name: 'Brand',
            labelKey: 'commerceAnalytics.productInsights.bestSeller.brand',
            tabs: ['brand_bestsellers'],
          },
        ],
      },
      {
        id: 'product_insights__top_competitors',
        titleKey: 'commerceAnalytics.productInsights.sections.topCompetitors',
        reportId: PRODUCT_INSIGHTS_REPORT_ID,
        topTabMode: true,
        hideChannels: true,
        build: topCompetitorsBuild,
        tabs: [
          {
            name: 'Ads',
            labelKey: 'commerceAnalytics.productInsights.topCompetitors.ads',
            tabs: TOP_COMPETITOR_AVAILABILITY_TABS,
            dropdowns: TOP_COMPETITOR_DROPDOWNS,
          },
          {
            name: 'All',
            labelKey: 'commerceAnalytics.productInsights.topCompetitors.all',
            tabs: TOP_COMPETITOR_AVAILABILITY_TABS,
            dropdowns: TOP_COMPETITOR_DROPDOWNS,
          },
          {
            name: 'Organic',
            labelKey: 'commerceAnalytics.productInsights.topCompetitors.organic',
            tabs: TOP_COMPETITOR_AVAILABILITY_TABS,
            dropdowns: TOP_COMPETITOR_DROPDOWNS,
          },
        ],
      },
      {
        // v3 DOM anchor `#priceSensitiveProducts`; gated on the two price columns
        // (`checkPriceSensitivitySchedule`, `ProductInsights.vue:113–115`).
        id: 'priceSensitiveProducts',
        titleKey: 'commerceAnalytics.productInsights.sections.priceSensitivity',
        reportId: PRODUCT_INSIGHTS_REPORT_ID,
        hideChannels: true,
        build: priceSensitivityBuild,
        tabs: [
          {
            name: 'Price Sensitivity Analysis',
            tabs: ['overview'],
            checkCols: ['suggested_price', 'product_price'],
          },
        ],
      },
      {
        // ORP-18953 follow-up: Demand Trend is product-level data, so it is available only when
        // the canonical product_bestsellers partition exists. category_bestsellers is never a
        // product-level fallback until backend verifies a separate semantic contract.
        id: 'product_insights__demand_trend',
        component: DemandTrendPanel,
        reportId: PRODUCT_INSIGHTS_REPORT_ID,
        tabs: [{ name: 'Demand Trend', tabs: ['product_bestsellers'] }],
      },
    ],
  },
]
